/**
 * Fixed top navigation bar. Hosts the wordmark + four anchor links that
 * smooth-scroll to each section. Uses useScrollSpy to highlight the active
 * section in brand blue.
 *
 * Mobile: same four links inline, smaller text + tighter gap. No hamburger
 * drawer — keeps the nav simple and visible at every breakpoint.
 */
import { useScrollSpy } from "../../hooks/useScrollSpy";
import logoUrl from "../../assets/anyangle_logo_sharp_even.svg";

const SECTIONS = [
  { id: "home", label: "Home" },
  { id: "capabilities", label: "Capabilities" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
] as const;

const SECTION_IDS = SECTIONS.map((s) => s.id);

export function Nav() {
  const active = useScrollSpy([...SECTION_IDS]);

  return (
    <header className="fixed inset-x-0 top-[var(--stage-banner-h)] z-50 border-b border-bg-border bg-bg-base/80 backdrop-blur-[5px]">
      <div className="mx-auto flex max-w-7xl items-end justify-between px-4 py-3 sm:px-6 sm:py-4">
        <a href="#home" className="flex items-end" aria-label="AnyAngle — home">
          <img
            src={logoUrl}
            alt="AnyAngle"
            className="h-5 w-auto sm:h-10"
            // Native dimensions (1890×372 ≈ 5.08:1) prevent CLS while the SVG
            // network request resolves; CSS overrides the rendered size.
            width={1890}
            height={372}
          />
        </a>
        <nav className="flex self-end items-center gap-2 translate-y-[3px] sm:gap-6 sm:translate-y-[10px]">
          {SECTIONS.map(({ id, label }) => {
            const isActive = active === id;
            return (
              <a
                key={id}
                href={`#${id}`}
                className={
                  "text-xs font-bold leading-none tracking-tight transition-colors sm:text-lg lg:text-2xl " +
                  (isActive
                    ? "text-brand-500"
                    : "text-white hover:text-slate-200")
                }
              >
                {label}
              </a>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
